import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Link as MuiLink
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Trophy, BarChart3, Globe, ArrowLeft } from 'lucide-react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  Event,
  EventEdition,
  getEvent,
  getAdjacentEditions
} from '../services/eventEditionService';
import { getRegistrationsByUserId, countActiveParticipants, countWaitingList } from '../services/registrationService';
import { getVerboseName } from '../services/codeListService';
import { getEventLogoUrl } from '../services/strapiService';
import { useUnits } from '../hooks/useUnits';
import { formatDistance, formatElevation } from '../utils/units';
import { getKrultraUrl } from '../config/urls';
import { useEventEdition } from '../contexts/EventEditionContext';

const EVENT_ID = 'kutc';

const formatCurrency = (value?: number) => {
  if (value === undefined || value === null) return '—';
  return `${value.toLocaleString('no-NO')} NOK`;
};


const KUTCOverviewPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { setEvent: setSelectedEvent } = useEventEdition();
  const units = useUnits();

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return t('editions.tba');
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString(i18n.language === 'no' ? 'nb-NO' : 'en-US', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };
  const [event, setEvent] = useState<Event | null>(null);
  const [previousEdition, setPreviousEdition] = useState<EventEdition | null>(null);
  const [nextEdition, setNextEdition] = useState<EventEdition | null>(null);
  const [previousEditionStatus, setPreviousEditionStatus] = useState<string>('');
  const [nextEditionStatus, setNextEditionStatus] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  const [userId, setUserId] = useState<string | null>(null);
  const [userRegistration, setUserRegistration] = useState<any | null>(null);
  const [activeParticipants, setActiveParticipants] = useState<number>(0);
  const [waitingListCount, setWaitingListCount] = useState<number>(0);
  const [availableSpots, setAvailableSpots] = useState<number | null>(null);
  const [isLoadingCounts, setIsLoadingCounts] = useState(true);

  const isRegistrationInvalid = userRegistration?.status === 'cancelled' || userRegistration?.status === 'expired';
  const hasActiveRegistration = Boolean(userRegistration && !isRegistrationInvalid);

  // Determine if new registrations should go on waiting-list
  const forceQueue = waitingListCount > 0;

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (u) => {
      setUserId(u?.uid || null);
    });
    return unsub;
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadMyReg = async () => {
      if (!userId || !nextEdition?.id) {
        if (isMounted) setUserRegistration(null);
        return;
      }
      try {
        const regs = await getRegistrationsByUserId(userId, nextEdition.id);
        if (!isMounted) return;
        setUserRegistration((regs || []).length > 0 ? regs[0] : null);
      } catch {
        if (isMounted) setUserRegistration(null);
      }
    };
    loadMyReg();
    return () => {
      isMounted = false;
    };
  }, [nextEdition?.id, userId]);

  useEffect(() => {
    let isMounted = true;
    const loadLogo = async () => {
      try {
        const url = await getEventLogoUrl(EVENT_ID);
        if (isMounted) setLogoUrl(url);
      } catch {
        if (isMounted) setLogoUrl(null);
      }
    };
    loadLogo();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch main event info
        const eventData = await getEvent(EVENT_ID);
        setEvent(eventData);

        // Fetch adjacent editions dynamically based on time
        const { previous, next } = await getAdjacentEditions(EVENT_ID);
        
        if (previous) {
          setPreviousEdition(previous);
          const statusLabel = await getVerboseName('eventEditions', 'status', previous.status, previous.status);
          setPreviousEditionStatus(statusLabel);
        }
        
        if (next) {
          setNextEdition(next);
          const statusLabel = await getVerboseName('eventEditions', 'status', next.status, next.status);
          setNextEditionStatus(statusLabel);
        }
      } catch (err: any) {
        console.error('Failed to load KUTC info:', err);
        setError(err?.message || t('errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [t]);

  // Fetch participant counts for next edition
  useEffect(() => {
    const fetchCounts = async () => {
      if (!nextEdition?.id) {
        setIsLoadingCounts(false);
        return;
      }
      
      try {
        const [activeCount, wlCount] = await Promise.all([
          countActiveParticipants(nextEdition.id),
          countWaitingList(nextEdition.id)
        ]);
        setActiveParticipants(activeCount);
        setWaitingListCount(wlCount);
        
        const maxParticipants = (nextEdition as any).maxParticipants ?? 0;
        if (maxParticipants > 0) {
          setAvailableSpots(Math.max(0, maxParticipants - activeCount));
        } else {
          setAvailableSpots(null);
        }
      } catch (error) {
        console.error('Error fetching participant counts:', error);
      } finally {
        setIsLoadingCounts(false);
      }
    };

    fetchCounts();
  }, [nextEdition?.id]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
        <Box mt={2}>
          <Button variant="contained" onClick={() => navigate('/')}>{t('errors.backToHome')}</Button>
        </Box>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="warning">{t('errors.notFound')}</Alert>
      </Container>
    );
  }

  // Check if edition has a dedicated page via RH_URL field, or fallback to known route pattern
  const previousEditionHasPage = previousEdition ? Boolean(previousEdition.RH_URL) : false;
  const nextEditionHasPage = nextEdition ? Boolean(nextEdition.RH_URL) : false;
  const activeRaceDistances = (event.raceDistances ?? []).filter((race) => {
    const { active } = race as { active?: boolean };
    return active !== false;
  });

  const isRegistrationOpenForEdition = (edition: EventEdition): boolean => {
    const now = new Date();
    const statusValue = String((edition as any).status ?? '').toLowerCase();
    const statusNumeric = parseInt(statusValue, 10);
    const STATUS_MAP: Record<string, number> = {
      hidden: 0, draft: 10, announced: 20, pre_registration: 30, open: 40,
      waitlist: 44, late_registration: 50, full: 54, closed: 60,
      in_progress: 70, suspended: 75, finished: 80, cancelled: 90, finalized: 100
    };
    const eventStatusCode = !isNaN(statusNumeric) ? statusNumeric : (STATUS_MAP[statusValue] ?? 0);

    const start = (edition.startTime as any)?.toDate ? (edition.startTime as any).toDate() : new Date(edition.startTime as any);
    const raceStarted = now >= start;

    const deadline = (edition.registrationDeadline as any)?.toDate
      ? (edition.registrationDeadline as any).toDate()
      : (edition.registrationDeadline ? new Date(edition.registrationDeadline as any) : null);
    const registrationDeadlinePassed = deadline ? now >= deadline : true;

    const isRegistrationPhase = [30, 40, 44, 50].includes(eventStatusCode);
    return isRegistrationPhase && !registrationDeadlinePassed && !raceStarted;
  };

  // Logic for showing participants list (same as KUTC 2026 page)
  const showParticipantsList = nextEdition && (() => {
    const now = new Date();
    const start = (nextEdition.startTime as any)?.toDate ? (nextEdition.startTime as any).toDate() : new Date(nextEdition.startTime as any);
    const raceStarted = now >= start;
    
    const statusValue = String((nextEdition as any).status ?? '').toLowerCase();
    const statusNumeric = parseInt(statusValue, 10);
    const STATUS_MAP: Record<string, number> = {
      hidden: 0, draft: 10, announced: 20, pre_registration: 30, open: 40,
      waitlist: 44, late_registration: 50, full: 54, closed: 60,
      in_progress: 70, suspended: 75, finished: 80, cancelled: 90, finalized: 100
    };
    const eventStatusCode = !isNaN(statusNumeric) ? statusNumeric : (STATUS_MAP[statusValue] ?? 0);
    
    const resultsStatusCode = String((nextEdition as any).resultsStatus || '').toLowerCase();
    const hasResultsAvailable = ['incomplete', 'preliminary', 'unofficial', 'final'].includes(resultsStatusCode) 
      || ['4', '5', '6', '7'].includes((nextEdition as any).resultsStatus || '');
    
    return activeParticipants > 0 && !raceStarted && !hasResultsAvailable;
  })();

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Back to Home Navigation */}
      <Button
        startIcon={<ArrowLeft />}
        onClick={() => navigate('/')}
        sx={{ mb: 2 }}
      >
        {t('events.backToHome')}
      </Button>
      {/* Header */}
      <Box textAlign="center" mb={6}>
        <Box display="flex" justifyContent="center" alignItems="center" gap={1.5} flexWrap="wrap" mb={1}>
          {logoUrl && (
            <Box
              component="img"
              src={logoUrl}
              alt="KUTC logo"
              sx={{ width: 44, height: 44, borderRadius: 1, objectFit: 'cover' }}
            />
          )}
          <Typography variant="h2" component="h1" fontWeight={800} gutterBottom sx={{ mb: 0 }}>
            {event.name || "Kruke's Ultra-Trail Challenge"}
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 640, mx: 'auto', mb: 3 }}>
          {t('kutc.tagline')}
        </Typography>
        
        {/* Quick Links */}
        <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap" mb={2}>
          <Button
            variant="contained"
            startIcon={<Globe />}
            href={getKrultraUrl('events/KUTC')}
          >
            {t('kutc.officialWebsite')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<BarChart3 />}
            onClick={() => navigate('/kutc/results')}
          >
            {t('events.results')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<BarChart3 />}
            onClick={() => navigate('/kutc/all-time')}
          >
            {t('kutc.allTimeLeaderboard')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Trophy />}
            onClick={() => navigate('/kutc/records')}
          >
            {t('events.records')}
          </Button>
        </Box>
      </Box>

      {/* Upcoming Event Section - Prominent at top without frame */}
      {nextEdition && (
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          {/* Emphasized Register Button */}
          <Box sx={{ mb: 3 }}>
            {userRegistration ? (
              <Button
                variant="contained"
                size="large"
                sx={{ 
                  px: 6, 
                  py: 2, 
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  boxShadow: 3
                }}
                onClick={() => {
                  void (async () => {
                    await setSelectedEvent(nextEdition.id);
                    navigate('/register');
                  })();
                }}
              >
                {hasActiveRegistration ? t('events.viewRegistration') : t('events.updateRegistration')}
              </Button>
            ) : (
              <>
                {isRegistrationOpenForEdition(nextEdition) && (availableSpots === 0 || forceQueue) && (
                  <Alert severity="warning" sx={{ mb: 2, maxWidth: 600 }}>
                    ⚠️ This event is fully booked. Sign in to join the waiting-list.
                  </Alert>
                )}
                <Button
                  variant="contained"
                  size="large"
                  color="primary"
                  disabled={!isRegistrationOpenForEdition(nextEdition) || isLoadingCounts}
                  sx={{ 
                    px: 6, 
                    py: 2, 
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: 3
                  }}
                  onClick={() => {
                    if (!isRegistrationOpenForEdition(nextEdition)) return;
                    void (async () => {
                      await setSelectedEvent(nextEdition.id);
                      if (userId) {
                        navigate('/register');
                      } else {
                        navigate('/auth?returnTo=/register');
                      }
                    })();
                  }}
                >
                  {userId 
                    ? (availableSpots === 0 || forceQueue ? t('events.joinWaitlist') : t('events.registerNow'))
                    : (availableSpots === 0 || forceQueue ? t('registration.signInToJoinWaitlist') : t('registration.signInToRegister'))
                  }
                </Button>
              </>
            )}
          </Box>

          {/* Date and Status Info */}
          <Typography variant="h6" sx={{ mb: 1 }}>
            <strong>{t('editions.event')}:</strong> {nextEdition.eventName}
          </Typography>
          <Typography variant="h6" sx={{ mb: 1 }}>
            <strong>{t('editions.date')}:</strong> {formatDate(nextEdition.startTime)}
          </Typography>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" component="span">
              <strong>{t('status.label')}:</strong>
            </Typography>
            <Chip label={nextEditionStatus} size="medium" color="primary" />
          </Box>

          {/* Go to Edition Button */}
          {nextEditionHasPage ? (
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate(nextEdition.RH_URL!)}
              sx={{ px: 4, py: 1.5 }}
            >
              {t('editions.viewEditionDetails', { edition: nextEdition.edition })}
            </Button>
          ) : (
            <Button variant="outlined" size="large" disabled sx={{ px: 4, py: 1.5 }}>
              {t('editions.detailsComingSoon')}
            </Button>
          )}

          {/* See Participants Button */}
          {showParticipantsList && (
            <Button
              variant="text"
              size="large"
              onClick={() => {
                void (async () => {
                  await setSelectedEvent(nextEdition.id);
                  navigate('/participants');
                })();
              }}
              sx={{ px: 4, py: 1.5, mt: 1 }}
            >
              {waitingListCount > 0 ? t('events.participantsAndWaitlist') : t('events.seeParticipants')}
            </Button>
          )}
        </Box>
      )}

      {/* Previous Edition Button - without frame */}
      {previousEdition && (
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          {previousEditionHasPage ? (
            <Button
              variant="text"
              onClick={() => navigate(previousEdition.RH_URL!)}
              sx={{ fontSize: '1rem' }}
            >
              {t('editions.viewEditionDetails', { edition: previousEdition.edition })}
            </Button>
          ) : (
            <Button variant="text" disabled>
              {t('editions.detailsComingSoon')}
            </Button>
          )}
        </Box>
      )}

      {/* Event Info */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        {/* Race Distances */}
        {activeRaceDistances.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                {t('kutc.raceDistances')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {activeRaceDistances.map((race) => (
                  <Grid item xs={6} key={race.id}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" fontWeight={700} color="primary">
                        {race.displayName}
                      </Typography>
                      <Typography variant="body2">
                        {formatDistance(race.length, units)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        +{formatElevation(race.ascent, units)}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                {t('kutc.lastOneStanding')}
              </Typography>
            </Paper>
          </Grid>
        )}
        
        {/* Fees & Participants */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {t('kutc.generalInfo')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {event.maxParticipants && (
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>{t('events.maxParticipants')}:</strong> {event.maxParticipants}
              </Typography>
            )}
            
            {event.fees && (
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  {t('kutc.registrationFees')}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>{t('kutc.participation')}:</strong> {formatCurrency(event.fees.participation)}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>{t('kutc.baseCamp')}:</strong> {formatCurrency(event.fees.baseCamp)}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>{t('kutc.depositRefundable')}:</strong> {formatCurrency(event.fees.deposit)}
                </Typography>
                <Typography variant="h6" sx={{ mt: 1 }}>
                  <strong>{t('kutc.total')}:</strong> {formatCurrency(event.fees.total)}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Footer Note */}
      <Box textAlign="center" sx={{ mt: 6 }}>
        <Typography variant="body2" color="text.secondary">
          {t('kutc.footerNote')}{' '}
          <MuiLink 
            href={getKrultraUrl('events/KUTC')} 
            sx={{
              color: (theme) => theme.palette.mode === 'dark' ? '#69A9E1' : '#4E82B4',
              '&:hover': {
                color: (theme) => theme.palette.mode === 'dark' ? '#8BC2F0' : '#41719C',
              }
            }}
          >
            {t('kutc.officialKutcWebsite')}
          </MuiLink>
        </Typography>
      </Box>
    </Container>
  );
};


export default KUTCOverviewPage;
